import dayjs from "dayjs";
import styles from "../LocationGyms/LocationGyms.module.scss";
import "../../components/ui/FormItemSelect/FormItemSelect.scss";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { InfoWindow, Marker } from "@react-google-maps/api";
import useAppSelector from "../../hooks/useAppSelector";
import useAppDispatch from "../../hooks/useAppDispatch";
import Map from "../../components/Map/Map";
import Loader from "../../components/shared/Loader/Loader";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import { OpenGymLabel } from "../../components/OpenGymLabel/OpenGymLabel";
import { ConfirmationForClassReservationPopup } from "../../components/ConfirmationForClassReservationPopup/ConfirmationForClassReservationPopup";
import { BookedSuccessfully } from "../../components/BookedSuccessfully/BookedSuccessfully";
import markIcon from "../../assets/images/icons/main-marker.svg";
import { defaultCenterForMap } from "../../components/Map/themeMap";
import { formatClassTime, formatGmtOffset } from "../../helper/date";
import { handleError } from "../../helper/errorHandler";
import { useGetClassEventsWithFiltersByGymIdQuery, useGetSettingsQuery } from "../../api/queries";
import { useClassEventReservationMutations, useOpenGymMutations } from "../../api/mutations";
import {
  IClassEvent,
  ClubpassEventType,
  IClassEventTimeRange,
  CLASS_LEVEL_CODES,
} from "../../models/ClassEvent";
import { IOpenGym } from "../../models/OpenGym";
import { IGymListing } from "../../models/GymListing";
import { useGetAllClassCategoriesQuery } from "../../api/queries/classEvent/useGetAllClassCategoriesQuery";
import { useGetTimeRangesForClassesQuery } from "../../api/queries/classEvent/useGetTimeRangesForClassesQuery";
import ClassEventsFilters from "./ClassEventFilters/ClassEventFilters";
import { NoClasses } from "../../components/NoClasses/NoClasses";
import LastMinuteDealLabel from "../../components/LastMinuteDealLabel/LastMinuteDealLabel";

const LocationGymClasses = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const gym = (location.state as { gym: IGymListing })?.gym;

  if (!gym) return null;

  const { user } = useAppSelector((s) => s.userProfileSlice);
  const { userSubscription } = useAppSelector((s) => s.userSubscriptionSlice);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [selectedTimes, setSelectedTimes] = useState<IClassEventTimeRange[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<IClassEvent | IOpenGym | null>(null);
  const [isUsingLoyalty, setIsUsingLoyalty] = useState<boolean>(false);

  const [isConfirmVisible, setConfirmVisible] = useState(false);
  const [isSuccessVisible, setSuccessVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [credits, setCredits] = useState<[number, number]>([0, 20]);

  const { data: settings = null } = useGetSettingsQuery();
  const { data: timeRangeOptions = [] } = useGetTimeRangesForClassesQuery();
  const { data: classCategories = [] } = useGetAllClassCategoriesQuery();

  const [approvedParams, setApprovedParams] = useState<any>({
    gymListingId: gym.id,
    userId: user?.id,
    eventDate: selectedDate.format("YYYY-MM-DD"),
  });

  const {
    data: classesByGym = [],
    isLoading: isLoadingClasses,
    isFetching,
    isRefetching,
    refetch,
  } = useGetClassEventsWithFiltersByGymIdQuery(approvedParams);

  const { createReservation } = useClassEventReservationMutations();
  const { createOpenGymReservation } = useOpenGymMutations();

  const onToggleLevel = (lvl: string) =>
    setSelectedLevels((prev) =>
      prev.includes(lvl) ? prev.filter((x) => x !== lvl) : [...prev, lvl]
    );

  const onSearch = () => {
    const categoryIdsCsv = selectedCategories.length ? selectedCategories.join(",") : undefined;

    const timeRangesCsv = selectedTimes.length
      ? selectedTimes.map((t) => t.id).join(",")
      : undefined;

    const classLevelsCsv = selectedLevels.length
      ? selectedLevels
          .map((lvl) => {
            const code = CLASS_LEVEL_CODES[lvl as keyof typeof CLASS_LEVEL_CODES];
            return code?.toString();
          })
          .filter(Boolean)
          .join(",")
      : undefined;

    const shouldSendCredits = !(credits[0] === 0 && credits[1] === 20);

    setApprovedParams({
      gymListingId: gym.id,
      userId: user?.id,
      ...(selectedDate && { eventDate: selectedDate.format("YYYY-MM-DD") }),
      ...(categoryIdsCsv && { categoryIds: categoryIdsCsv }),
      ...(timeRangesCsv && { timeRanges: timeRangesCsv }),
      ...(classLevelsCsv && { classLevels: classLevelsCsv }),
      ...(shouldSendCredits && { minCredits: credits[0], maxCredits: credits[1] }),
    });
  };

  const onResetAll = () => {
    setSelectedCategories([]);
    setSelectedDate(dayjs());
    setSelectedTimes([]);
    setSelectedLevels([]);
    setCredits([0, 20]);

    setApprovedParams({
      gymListingId: gym.id,
      userId: user?.id,
    });

    refetch();
  };

  const onSelectClassEvent = (item: IClassEvent | IOpenGym) => {
    if (!item.isAvailable) return;
    setSelectedEvent(item);
    setConfirmVisible(true);
    setIsUsingLoyalty(false);
  };

  const onCloseConfirm = () => {
    setConfirmVisible(false);
    setIsUsingLoyalty(false);
    setSelectedEvent(null);
  };

  const onCloseSuccess = () => setSuccessVisible(false);

  const onConfirmBookingMain = async () => {
    if (!userSubscription) {
      navigate("/settings/account/manage-plan");
      return;
    }

    if (!selectedEvent) return;

    try {
      setSubmitting(true);
      if (selectedEvent?.type === ClubpassEventType.GROUP_CLASS) {
        await createReservation({
          gymListingId: gym.id,
          classEventId: selectedEvent.id,
          useLoyaltyPoints: isUsingLoyalty,
        });
      } else {
        await createOpenGymReservation({
          gymListingId: gym.id,
          // @ts-expect-error API model mismatch
          classEventId: selectedEvent.classEventId,
          startDate: selectedEvent.occuranceDate,
          // @ts-expect-error API model mismatch
          endDate: selectedEvent.endDate,
          useLoyaltyPoints: isUsingLoyalty,
        });
      }
      setConfirmVisible(false);
      setSuccessVisible(true);
      refetch();
    } catch (e) {
      handleError(e, dispatch, t);
    } finally {
      setSubmitting(false);
      setIsUsingLoyalty(false);
    }
  };

  const renderGymList = () =>
    isLoadingClasses || isFetching || isRefetching ? (
      <Loader />
    ) : classesByGym && classesByGym.length === 0 ? (
      <NoClasses />
    ) : (
      classesByGym.map((classEvent) => {
        const clubTz: string | null = null;
        const timeLabel = formatClassTime(classEvent.occuranceDate, clubTz);
        const gmtLabel = formatGmtOffset(classEvent.occuranceDate, clubTz);

        return (
          <div
            key={classEvent.id}
            className={styles.gymItem}
            onClick={() => onSelectClassEvent(classEvent)}
          >
            <div className={styles.left}>
              <div>
                {classEvent.hasLastMinDiscount && <LastMinuteDealLabel />}
                <p>
                  {gmtLabel} {timeLabel}
                </p>
                {classEvent.type === ClubpassEventType.GROUP_CLASS && (
                  <p style={{ marginTop: 12 }}>
                    {classEvent.duration} {t("Min").toLowerCase()}
                  </p>
                )}
              </div>
              <div className={styles.info}>
                <p>{classEvent.name}</p>
                <p>{classEvent.roomName}</p>
                {classEvent.type === ClubpassEventType.OPEN_GYM && (
                  <div style={{ width: 100 }}>
                    <OpenGymLabel />
                  </div>
                )}
              </div>
            </div>

            {classEvent.isAvailable ? (
              <CustomButton
                text={
                  userSubscription
                    ? `${classEvent.credits || "0"} ${t("Credits")}`
                    : t("BuySubscription")
                }
                type="button"
                typeStyle="outline"
                style={{ width: 200, margin: "auto" }}
              />
            ) : (
              <div className={styles.notAvailable}>{t("NotAvailable")}</div>
            )}
          </div>
        );
      })
    );

  return (
    <>
      <Helmet>
        <title>{t("LocationsAccessTopFitnessCentersNearYouWithClubPass")}</title>
      </Helmet>

      <div className={styles.container}>
        <div className={styles.top}>
          <div className="container">
            <ClassEventsFilters
              credits={credits}
              selectedDate={selectedDate}
              selectedLevels={selectedLevels}
              selectedTimes={selectedTimes}
              settings={settings}
              allCategories={classCategories}
              categories={selectedCategories}
              timeRangeOptions={timeRangeOptions}
              onResetAll={onResetAll}
              onSearch={onSearch}
              onSelectDate={setSelectedDate}
              onSelectTimes={setSelectedTimes}
              onCreditsChange={setCredits}
              onToggleLevel={onToggleLevel}
              onSelectCategories={setSelectedCategories}
            />
          </div>
        </div>

        <div className={styles.wrapper}>
          <div className={styles.gymInfo}>{renderGymList()}</div>

          <div className={styles.map}>
            <Map
              center={{
                lat: gym.lat ?? defaultCenterForMap.lat,
                lng: gym.long ?? defaultCenterForMap.lng,
              }}
            >
              <Marker position={{ lat: gym.lat, lng: gym.long }} icon={markIcon} />
              <InfoWindow
                position={{ lat: gym.lat, lng: gym.long }}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -30),
                }}
              >
                <div className={styles.hoverBox}>
                  <div
                    className={styles.image}
                    style={{ backgroundImage: `url("${gym.imagePaths[0]}")` }}
                  />
                  <div className={styles.services}>
                    {JSON.parse(gym.facilityOffers)
                      .map((x: string) => t(x))
                      .slice(0, 2)
                      .join(", ")}
                  </div>
                  <h3 className={styles.title}>{gym.businessName}</h3>
                  <p className={styles.address}>
                    {gym.street} {gym.streetNumber}, {gym.city}
                  </p>
                </div>
              </InfoWindow>
            </Map>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <ConfirmationForClassReservationPopup
          open={isConfirmVisible}
          title={selectedEvent?.name || ""}
          startISO={selectedEvent?.occuranceDate || ""}
          endISO={
            selectedEvent.type === ClubpassEventType.OPEN_GYM
              ? // @ts-expect-error backend diff
                selectedEvent.endDate
              : null
          }
          refundCancellationHours={settings?.refundCancellationHours || 0}
          onClose={onCloseConfirm}
          onConfirm={onConfirmBookingMain}
          durationMin={
            selectedEvent.type === ClubpassEventType.GROUP_CLASS ? selectedEvent?.duration : null
          }
          instructor={
            selectedEvent.type === ClubpassEventType.GROUP_CLASS
              ? selectedEvent?.instructorFullName
              : null
          }
          credits={selectedEvent?.credits}
          isLoyaltyDealAvailable={selectedEvent.isLoyaltyDealAvailable}
          setIsUsingLoyalty={setIsUsingLoyalty}
          loading={submitting}
        />
      )}

      <BookedSuccessfully
        isOpenGym={selectedEvent?.type === ClubpassEventType.OPEN_GYM}
        open={isSuccessVisible}
        onClose={onCloseSuccess}
      />
    </>
  );
};

export default LocationGymClasses;
